<template>
  <footer class="footer dark">
    <div class="container main">
      <div class="row">
        <div class="col-12 col-xl-6">
          <Link :href="route('index')">
            <img src="/images/logo/logo-full-white.svg" class="logo" alt="Veximus Technologies" />
          </Link>
          <p>{{ t('Malaysia #1 software startup based in Petaling Jaya that specialised in developing premium websites, mobile applications and other software with the lowest price guaranteed.') }}</p>
        </div>

        <div class="col-6 col-md-4 col-xl-2">
          <nav class="footer-nav" aria-labelledby="footer-services">
            <h2 id="footer-services">{{ t('Services') }}</h2>
            <ul>
              <li>
                <Link :href="route('web-design')">{{ t('Web design') }}</Link>
              </li>
              <li>
                <Link :href="route('digital-marketing')">{{ t('Digital marketing') }}</Link>
              </li>
              <li>
                <Link :href="route('mobile-application')">{{ t('Mobile application') }}</Link>
              </li>
              <li>
                <Link :href="route('ai-solutions')">{{ t('AI Solutions') }}</Link>
              </li>
              <li>
                <Link :href="route('brand-presence')">{{ t('Brand presence') }}</Link>
              </li>
            </ul>
          </nav>
        </div>

        <!-- Explore and Legal are one column on a phone, so Legal reads directly
             under Explore rather than wrapping back to the left of the page and
             leaving the right half of the row empty beside it. Nested, the pair
             splits back into two columns of the same width as Services from md
             up, so the wide grid is unchanged: 6 + 2 + 2 + 2 at xl. -->
        <div class="col-6 col-md-8 col-xl-4">
          <div class="row">
            <div class="col-12 col-md-6">
              <nav class="footer-nav" aria-labelledby="footer-explore">
                <h2 id="footer-explore">{{ t('Explore') }}</h2>
                <ul>
                  <li>
                    <Link :href="route('portfolios')">{{ t('Portfolios') }}</Link>
                  </li>
                  <li>
                    <Link :href="route('promotions')">{{ t('Promotions') }}</Link>
                  </li>
                  <li>
                    <Link :href="route('blog.index')">{{ t('Blog') }}</Link>
                  </li>
                  <li>
                    <!-- A document rather than a page: a plain anchor, because an
                         Inertia `<Link>` would try to navigate the SPA to a PDF,
                         and a new tab so the reader keeps the site they were
                         reading. It is served from `public/pdf`, which is the
                         only directory the web root can reach. -->
                    <a href="/pdf/service-catalog.pdf" target="_blank" rel="noopener">{{ t('Catalog') }}</a>
                  </li>
                </ul>
              </nav>
            </div>

            <div class="col-12 col-md-6">
              <nav class="footer-nav" aria-labelledby="footer-legal">
                <h2 id="footer-legal">{{ t('Legal') }}</h2>
                <ul>
                  <li>
                    <Link :href="route('terms-of-use')">{{ t('Terms of Use') }}</Link>
                  </li>
                  <li>
                    <Link :href="route('privacy-policy')">{{ t('Privacy Policy') }}</Link>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
      <div class="row contact">
        <div class="col-12 col-md-4 col-lg-4">
          <div>{{ t('Client Hotline') }}</div>
          <a :href="phoneHref" rel="noopener">{{ dial }}</a>
        </div>
        <div class="col-12 col-md-6 col-lg-4">
          <div>{{ t('Support Email') }}</div>
          <a :href="emailHref" rel="noopener">{{ business.email }}</a>
        </div>
        <div class="col-12 col-md-12 col-lg-4">
          <div class="icons">
            <!-- The profile URLs come from `seo.profiles` through the shared
                 `business` prop, which is also what the Organization node in the
                 structured data points at with `sameAs`. They used to be typed in
                 here as well, so the visible links and the structured data were two
                 copies of the same five URLs. -->
            <a
              v-for="social in socials"
              :key="social.key"
              :href="social.href"
              :aria-label="social.label"
              target="_blank"
              rel="noopener"
            >
              <font-awesome-icon :icon="['fab', social.icon]" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="container-fluid secondary">
      <div class="container p-0">
        <div class="row">
          <div class="col-12 col-xl-8">
            <div>
              <Link :href="route('index')">{{ business.name }}. ({{ business.registration }})</Link>
              &copy; {{ new Date().getFullYear() }} {{ t('All rights reserved.') }}
            </div>
            {{ t('The information posted on this site is covered by the Intellectual Property Protection of Malaysia. Use of materials is only allowed with the written permission of the owners, the reference to the source is obligated. All trademarks displayed on this site are the property of their respective owners.') }}
          </div>
          <div class="col-12 col-xl-4 d-none d-xl-block">
            <div class="partners">
              <img src="/images/partners/eghl.svg" class="partner" alt="eGHL" />
              <img src="/images/partners/stripe.svg" class="partner" alt="Stripe" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.footer {
  background: $ground-dark;
  color: $on-dark-body;
  font-size: 0.9rem;
  position: relative;

  /* `.dark` is carried as a marker rather than for any rule declared here: the
     footer sets its own ground above. The fixed rails ask which band is behind
     them through `composables/useSurface.js`, and the footer is one of the
     bands, so it has to say so the same way the sections do. */

  .main {
    padding-top: 3rem;
    padding-bottom: 3rem;

    .logo {
      height: 30px;
      margin: 0.5rem 0 1.5rem;
      opacity: 0.75;
    }

    .footer-nav {
      /* The column heading is an h2, not an h6. The footer nav is its own
         landmark, and jumping from the page's h1 to an h6 skipped four levels
         for the only headings the footer has. Its treatment is the shared
         $label-* step: it is the same small uppercase label the rest of the
         site uses, and it takes the near-black ground's colour from there. It
         used to sit one step down at 0.72rem for no reason other than that the
         footer had written its own copy. */
      h2 {
        color: $label-on-dark;
        font-family: $headings-font-family;
        font-size: $label-size;
        font-weight: $label-weight;
        letter-spacing: $label-tracking;
        margin-bottom: 1.1rem;
        text-transform: uppercase;
      }

      ul {
        list-style: none;
        margin: 0;
        padding: 0;
      }

      li + li {
        margin-top: 0.1rem;
      }

      a {
        color: $on-dark-body;
        display: inline-block;
        font-family: $headings-font-family;
        font-size: 0.9rem;
        font-weight: 500;
        padding: 0.35rem 0;
        position: relative;
        transition: color $dur-quick $ease, transform $dur-quick $ease;

        /* Hairline that wipes in from the left on hover, the same light blue
           as the column heading so the whole block reads as one unit. */
        &::after {
          background: $dark-accent;
          bottom: 0.2rem;
          content: '';
          height: 1px;
          left: 0;
          position: absolute;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform $dur-quick $ease;
          width: 100%;
        }

        &:hover {
          color: $on-dark-strong;
          transform: translateX(2px);

          &::after {
            transform: scaleX(1);
          }
        }
      }

      /* The rows are a ~28px target. A coarse pointer gets one worth tapping; a
         mouse keeps the footer's own rhythm. The hairline moves with the padding
         so it still reads as sitting under the words. */
      @media (pointer: coarse) {
        a {
          padding: 0.7rem 0;

          &::after {
            bottom: 0.5rem;
          }
        }
      }

      /* The brand block spans the full row until xl, so each nav group needs
         its own top gap for as long as it sits below the brand rather than
         beside it. */
      @media (max-width: 1199.98px) {
        margin-top: 2rem;
      }
    }

    .contact {
      border-top: solid 1px $on-dark-line-strong;
      margin-top: 2rem;
      padding-top: 2rem;

      a {
        color: $primary;
        font-family: $headings-font-family;
        font-size: 1.25rem;
        letter-spacing: -0.03rem;
      }
    }

    .icons {
      display: flex;
      justify-content: flex-end;

      a {
        align-items: center;
        background: $primary;
        border-radius: $r-lg;
        color: $on-dark-strong;
        display: flex;
        height: 50px;
        justify-content: center;
        margin: 0 0.15rem;
        width: 50px;

        @media (max-width: 575.98px) {
          border-radius: $r-md;
          height: 44px;
          width: 44px;
        }
      }

      @media (max-width: 991.98px) {
        justify-content: left;
        margin-top: 2rem;
      }
    }
  }

  .secondary {
    background: $on-dark-fill;
    color: $on-dark-muted;
    font-size: 0.65rem;
    font-weight: 300;
    padding-top: 1.5rem;
    padding-bottom: 2rem;

    .partners {
      display: flex;
      justify-content: flex-end;

      .partner {
        margin: 0 0.5rem;
        object-fit: contain;
        opacity: 0.25;
        width: 80px;

        &:nth-child(2) {
          padding-top: 0.25rem;
        }
      }
    }
  }
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import { useI18n } from 'vue-i18n';
import { whatsappHref } from '@/whatsapp';

export default {
  components: {
    Link
  },
  computed: {
    business() {
      return this.$page.props.business;
    },

    /* One number, three uses. The digits come from the shared business prop
       (the same value the invoice footer prints); `dial` is what the visitor
       reads, `phoneHref` is the tel: target and `whatsappHref` is the wa.me
       link. Previously the href said +60 19-291 8967, the text said
       +603 7624 8096 and the WhatsApp link said +60 3 7624 8096. */
    digits() {
      return String(this.business.phone || '').replace(/[^0-9]/g, '');
    },

    dial() {
      return this.business.phone || '';
    },

    phoneHref() {
      return 'tel:+' + this.digits;
    },

    emailHref() {
      return 'mailto:' + this.business.email;
    },

    whatsappHref() {
      return whatsappHref(this.business.phone, this.$page.props.locale, this.$page.props.referral);
    },

    /* The footer's icons, in the order the design has always shown them.
       WhatsApp sits third among the profiles because its URL is derived from the
       company phone number rather than listed with the rest, so it is placed here
       instead of being carried in the shared profile list.

       The order and the icon names stay in the view: the server owns which
       accounts exist, the footer owns how they are drawn and in what order. A
       profile with no URL configured is dropped rather than rendered as a link
       with an empty href. */
    socials() {
      const order = ['facebook', 'instagram', 'whatsapp', 'twitter', 'youtube', 'linkedin'];

      const icons = {
        facebook: 'facebook-f',
        instagram: 'instagram',
        whatsapp: 'whatsapp',
        twitter: 'twitter',
        youtube: 'youtube',
        linkedin: 'linkedin-in'
      };

      /* The link holds only an icon, so the accessible name is here rather than
         in the markup. Brand names rather than translated words, because the
         network is called the same thing in all three languages and the icon is
         what a reader recognises. */
      const labels = {
        facebook: 'Facebook',
        instagram: 'Instagram',
        whatsapp: 'WhatsApp',
        twitter: 'Twitter',
        youtube: 'YouTube',
        linkedin: 'LinkedIn'
      };

      const profiles = this.business.profiles || {};

      return order
        .map((key) => ({
          key,
          icon: icons[key],
          label: labels[key],
          href: key === 'whatsapp' ? this.whatsappHref : profiles[key]
        }))
        .filter((social) => social.href);
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'All rights reserved.': 'All rights reserved.',
          'Catalog': 'Catalog',
          'Client Hotline': 'Client Hotline',
          Explore: 'Explore',
          'Malaysia #1 software startup based in Petaling Jaya that specialised in developing premium websites, mobile applications and other software with the lowest price guaranteed.': 'Malaysia #1 software startup based in Petaling Jaya that specialised in developing premium websites, mobile applications and other software with the lowest price guaranteed.',
          'Support Email': 'Support Email',
          'The information posted on this site is covered by the Intellectual Property Protection of Malaysia. Use of materials is only allowed with the written permission of the owners, the reference to the source is obligated. All trademarks displayed on this site are the property of their respective owners.': 'The information posted on this site is covered by the Intellectual Property Protection of Malaysia. Use of materials is only allowed with the written permission of the owners, the reference to the source is obligated. All trademarks displayed on this site are the property of their respective owners.',
        },
        'zh-MY': {
          'All rights reserved.': '版权所有。',
          'Catalog': '服务目录',
          'Client Hotline': '客户热线',
          Explore: '探索更多',
          'Malaysia #1 software startup based in Petaling Jaya that specialised in developing premium websites, mobile applications and other software with the lowest price guaranteed.': '微星科技是马来西亚排名第一的软件初创公司，成立于雪兰莪州的八打灵再也。我们自从成立以来便致力于以最低的价格为我们的客户开发最优质的网站，移动应用程序以及其他软件。',
          'Support Email': '电邮地址',
          'The information posted on this site is covered by the Intellectual Property Protection of Malaysia. Use of materials is only allowed with the written permission of the owners, the reference to the source is obligated. All trademarks displayed on this site are the property of their respective owners.': '本站上发布的所有信息皆受马来西亚知识产权保护局所保护。你仅能够在所有者的书面许可下，方可使用本站材料与素材，所有材料与素材的使用必须注明出处。本站上显示的所有商标均为其各自所有者的财产。',
        },
        'ms-MY': {
          'All rights reserved.': 'Hak Cipta Terpelihara.',
          'Catalog': 'Katalog',
          'Client Hotline': 'Talian Pelanggan',
          Explore: 'Terokai',
          'Malaysia #1 software startup based in Petaling Jaya that specialised in developing premium websites, mobile applications and other software with the lowest price guaranteed.': 'Syarikat teknologi permulaan #1 di Malaysia yang berpusat di Petaling Jaya, Selangor. Kami pakar dalam bidang teknologi, khususnya membekalkan laman web premium, aplikasi mudah alih dengan jaminan harga yang paling rendah.',
          'Support Email': 'Alamat Emel',
          'The information posted on this site is covered by the Intellectual Property Protection of Malaysia. Use of materials is only allowed with the written permission of the owners, the reference to the source is obligated. All trademarks displayed on this site are the property of their respective owners.': 'Semua maklumat yang diterbitkan di laman web ini dilindungi oleh Agensi Perlindungan Harta Intelek Malaysia. Penggunaan bahan hanya dibenarkan dengan izin bertulis dari pemilik, rujukan ke sumber wajib dilakukan. Semua logo yang dipaparkan di laman web ini adalah hak milik pemiliknya masing-masing.',
        }
      }
    });

    return { t };
  }
};
</script>
